var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { JwtToken } from "./jwt";
import { ProofKeyCodeEx } from "./pkce";
import { CsrfToken } from "./csrf";
import { CognitoJwtVerifier } from "aws-jwt-verify";
import { URLParams, Redirect, Cookie, LocalStorage, isDefined } from "./utils";
/**
 * A class for managing the user's auth.
 */
var User = /** @class */ (function () {
    function User() {
        var _this = this;
        this.userAuthorizationItems = {};
        /**
         * Asynchronous function that validates user tokens and updates the
         * user's authorization status.
         * @param userPoolId - the Cognito user pool id
         * @param clientId - the Cognito app client id
         */
        this.isUserAuthorized = function (userPoolId, clientId) { return __awaiter(_this, void 0, void 0, function () {
            var verifier, accessToken, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        verifier = CognitoJwtVerifier.create({
                            userPoolId: userPoolId,
                            clientId: clientId,
                            tokenUse: "access",
                        });
                        accessToken = this.getAccessToken();
                        return [4 /*yield*/, verifier.verify(accessToken)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _a.sent();
                        if (error_1.message.includes("Token expired")) {
                            throw new Error("Token expired");
                        }
                        this.updateAuthorizationStatus(false);
                        return [2 /*return*/];
                    case 3:
                        this.updateAuthorizationStatus(true);
                        return [2 /*return*/];
                }
            });
        }); };
        /**
         * Asynchronous function that executes token exchange for an
         * authenticated user and sets short-lived authorization cookies
         * and CloudFront signed cookies.
         * @param authorizationCode - the code returned to the user after successful authentication
         * @param clientId - the Cogntio app client id
         * @param redirectUri - the url that a user is directed to after successful authentication
         * @param tokenUrl - the url to exchange tokens
         * @param commonDomain - the domain used to set cookies
         */
        this.authorizeUser = function (authorizationCode, clientId, redirectUri, tokenUrl, commonDomain) { return __awaiter(_this, void 0, void 0, function () {
            var pkce, payload, tokens;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        pkce = ProofKeyCodeEx.getChallengeFromStorage();
                        payload = {
                            grant_type: "authorization_code",
                            client_id: clientId,
                            code: authorizationCode,
                            redirect_uri: redirectUri,
                            code_verifier: pkce.codeVerifier,
                        };
                        return [4 /*yield*/, JwtToken.getTokens(tokenUrl, payload)];
                    case 1:
                        tokens = _a.sent();
                        return [4 /*yield*/, this.setUserAuthorizationItems(tokens)];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.setShortLivedAuthorizationCookies(commonDomain)];
                    case 3:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); };
        /**
         * Redirects user to identity provider's authentication service.
         * @param clientId - the Cognito app client id
         * @param redirectUri - the url that a user is directed to after successful authentication
         * @param identityProvider - the identity provider the user will authenticate with
         * @param ssoLoginUrl - the url to redirect the user for active directory authentication
         */
        this.authenticateUser = function (clientId, redirectUri, identityProvider, ssoLoginUrl) {
            var stateToken = CsrfToken.generateStateToken();
            var pkce = ProofKeyCodeEx.generateChallenge();
            var params = {
                code_challenge_method: "S256",
                response_type: "code",
                client_id: clientId,
                redirect_uri: redirectUri,
                identity_provider: identityProvider,
                state: stateToken,
                code_challenge: pkce.codeChallenge,
            };
            ssoLoginUrl = URLParams.update(ssoLoginUrl, params);
            Redirect.redirect(ssoLoginUrl);
        };
        /**
         * Invalidates a user's session credentials
         */
        this.invalidateUser = function () {
            LocalStorage.removeItem("userAuthorizationItems");
            Cookie.delete("CloudFront-Signature");
            Cookie.delete("CloudFront-Key-Pair-Id");
            Cookie.delete("CloudFront-Policy");
            Cookie.delete("userDetails");
            Cookie.delete("accessToken");
            Cookie.delete("identityToken");
            Cookie.delete("refreshToken");
        };
        /**
         * Asynchronous function to refresh tokens.
         * @param tokenUrl - the url for exchanging tokens
         * @param clientId - the Cognito app client id
         */
        this.refreshUserTokens = function (clientId, tokenUrl) { return __awaiter(_this, void 0, void 0, function () {
            var refreshToken, payload, tokens;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        refreshToken = this.getRefreshToken();
                        payload = {
                            grant_type: "refresh_token",
                            client_id: clientId,
                            refresh_token: refreshToken,
                        };
                        return [4 /*yield*/, JwtToken.getTokens(tokenUrl, payload)];
                    case 1:
                        tokens = _a.sent();
                        this.updateUserTokensInLocalStorage(tokens);
                        return [2 /*return*/];
                }
            });
        }); };
        /**
         * Reads user authoriation items from cookies.
         * @returns an object containing user authorization items
         */
        this.readUserAuthorizationCookies = function () {
            var userAuthorizationItems = {};
            var accessToken = Cookie.get("accessToken");
            var identityToken = Cookie.get("identityToken");
            var refreshToken = Cookie.get("refreshToken");
            var userDetails = Cookie.get("userDetails");
            if (isDefined(accessToken) &&
                isDefined(identityToken) &&
                isDefined(refreshToken) &&
                isDefined(userDetails)) {
                userAuthorizationItems.isAuthorized = true;
                userAuthorizationItems.userTokens = {};
                userAuthorizationItems.userTokens.accessToken = accessToken;
                userAuthorizationItems.userTokens.identityToken = identityToken;
                userAuthorizationItems.userTokens.refreshToken = refreshToken;
                userAuthorizationItems.userDetails = JSON.parse(userDetails);
            }
            return userAuthorizationItems;
        };
        /**
         * Reads user authoriation items from local storage.
         * @returns an object containing user authorization items
         */
        this.getUserAuthorizationItemsFromLocalStorage = function () {
            var userAuthorizationItems = JSON.parse(LocalStorage.getItem("userAuthorizationItems"));
            if (!isDefined(userAuthorizationItems)) {
                return {};
            }
            return userAuthorizationItems;
        };
        /**
         * Updates the user tokens in local storage.
         * @param tokens - an object containing the user's access, identity, and refresh tokens
         */
        this.updateUserTokensInLocalStorage = function (tokens) {
            var userAuthorizationItems = _this.getUserAuthorizationItemsFromLocalStorage();
            userAuthorizationItems.userTokens.identityToken = tokens.id_token;
            userAuthorizationItems.userTokens.accessToken = tokens.access_token;
            _this.writeUserAuthorizationItemsToLocalStorage(userAuthorizationItems);
        };
        /**
         * Updates the user's authorization status boolean.
         */
        this.updateAuthorizationStatus = function (status) {
            var userAuthorizationItems = _this.getUserAuthorizationItemsFromLocalStorage();
            userAuthorizationItems.isAuthorized = status;
            _this.writeUserAuthorizationItemsToLocalStorage(userAuthorizationItems);
        };
        /**
         * Loads user tokens and details into authorization items object. Saves authorization
         * items in local storage.
         * @param tokens - an object containing the user's access, identity, and refresh tokens
         */
        this.setUserAuthorizationItems = function (tokens) { return __awaiter(_this, void 0, void 0, function () {
            var decodedToken, roles;
            return __generator(this, function (_a) {
                this.userAuthorizationItems.isAuthorized = true;
                this.userAuthorizationItems.userTokens = {
                    accessToken: tokens.access_token,
                    refreshToken: tokens.refresh_token,
                    identityToken: tokens.id_token
                };
                decodedToken = JwtToken.decode(tokens.id_token);
                roles = decodedToken["custom:UserRole"]
                    .replace(/[[\] ']+/g, "")
                    .split(",");
                if (roles.length === 1 && roles[0] === "") {
                    throw new Error("Unauthorized");
                }
                this.userAuthorizationItems.userDetails = {
                    roles: roles,
                    userId: decodedToken.identities[0].userId,
                    userName: decodedToken["cognito:username"],
                    displayName: "".concat(decodedToken.given_name, " ").concat(decodedToken.family_name),
                };
                this.writeUserAuthorizationItemsToLocalStorage(this.userAuthorizationItems);
                return [2 /*return*/];
            });
        }); };
        /**
         * Stores user authorization items in local storage.
         * @param userAuthorizationItems - the tokens and claims of the user
         */
        this.writeUserAuthorizationItemsToLocalStorage = function (userAuthorizationItems) {
            LocalStorage.setItem("userAuthorizationItems", JSON.stringify(userAuthorizationItems));
        };
        /**
         * Reads the refresh token from local storage.
         * @returns the stored refresh token
         */
        this.getRefreshToken = function () {
            return _this.getUserAuthorizationItemsFromLocalStorage().userTokens
                .refreshToken;
        };
        /**
         * Reads the access token from local storage.
         * @returns the stored access token.
         */
        this.getAccessToken = function () {
            return _this.getUserAuthorizationItemsFromLocalStorage().userTokens
                .accessToken;
        };
        /**
         * Stores user authorization items in a short lived cookie
         * @param commonDomain - the domain used to set cookies
         */
        this.setShortLivedAuthorizationCookies = function (commonDomain) { return __awaiter(_this, void 0, void 0, function () {
            var userAuthorizationItems;
            return __generator(this, function (_a) {
                userAuthorizationItems = this.getUserAuthorizationItemsFromLocalStorage();
                Cookie.set("accessToken", userAuthorizationItems.userTokens.accessToken, "10", commonDomain);
                Cookie.set("identityToken", userAuthorizationItems.userTokens.identityToken, "10", commonDomain);
                Cookie.set("refreshToken", userAuthorizationItems.userTokens.refreshToken, "10", commonDomain);
                Cookie.set("userDetails", JSON.stringify(userAuthorizationItems.userDetails), "10", commonDomain);
                return [2 /*return*/];
            });
        }); };
    }
    return User;
}());
export { User };
